import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, IconButton, Button,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar,
  FormControlLabel,
  Tooltip, Divider, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Autocomplete, Avatar, useMediaQuery,
  TablePagination, Stack, useTheme, Accordion, AccordionSummary, AccordionDetails,
  Checkbox, FormGroup,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Share as ShareIcon, Edit as EditIcon, Delete as DeleteIcon,
  Close as CloseIcon, Refresh as RefreshIcon, Print as PrintIcon,
  Person as PersonIcon, Visibility as ViewIcon,
  Description as DescIcon,
  FavoriteBorder as HeartIcon, Thermostat as TempIcon,
  MonitorWeight as WeightIcon, Height as HeightIcon,
  Timeline as TimelineIcon, Assignment as AssignIcon,
  CheckCircle as SignedIcon, Create as DraftIcon,
  CalendarMonth as CalendarIcon,
  LocalHospital as HospitalIcon,
  Bloodtype as BloodIcon,
  Air as LungIcon,
  Speed as SpeedIcon,
  AutoAwesome as AiIcon,
  ExpandMore as ExpandMoreAccordionIcon,
  NoteAdd as NoteAddIcon,
  Layers as LayersIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { useAuth } from '../../contexts/AuthContext';
import SendDocumentDialog from '../../components/SendDocumentDialog';
import ClinicalHtmlField from '../../components/ClinicalHtmlField';
import { resolveEvolutionEntryFormat } from '../../utils/clinicEvolutionFormat';
import { sanitizeClinicalHtml } from '../../utils/clinicalHtml';
import { buildCertificatePrintHtml } from '../../utils/certificatePrintDocument';
import { buildPrescriptionPrintHtml } from '../../utils/prescriptionPrintDocument';
import { getLoggedInProfessionalOption, mergeLoggedInProfessionalIntoOptions } from '../../utils/loggedInProfessional';
import EvolutionPrintablesWizard from '../../components/evolution/EvolutionPrintablesWizard';
import EvolutionIndicationsDialog from '../../components/evolution/EvolutionIndicationsDialog';
import {
  defaultEvolutionReturnWorkflow,
  type EvolutionAttachedPrintable,
  type EvolutionPrintableCategory,
  type EvolutionReturnWorkflow,
} from '../../components/evolution/evolutionWorkflowTypes';
import { buildEvolutionPrintBatch, evolutionHasPrintBatchContent } from '../../components/evolution/mergeEvolutionPrintBatch';
import {
  evolutionViewerHasFollowUpSection,
  evolutionPrintableListLabel,
  formatEvolutionReturnWorkflowLines,
  getEvolutionReturnWorkflow,
  getEvolutionWorkflowPrintables,
  normalizeEvolutionProcedureLabels,
} from '../../components/evolution/evolutionViewHelpers';
import {
  buildClinicPrintDocumentHtml,
  buildClinicMultiSheetPrintHtml,
  buildEvolutionPrintAttachmentSection,
  escapeHtml,
} from '../../utils/clinicPrintLetterhead';
import { printClinicalEvolutionFullDocument } from '../../utils/clinicalEvolutionFullPrint';
import { useSearchParams } from 'react-router-dom';

interface VitalSigns {
  blood_pressure_systolic?: number;
  blood_pressure_diastolic?: number;
  heart_rate?: number;
  respiratory_rate?: number;
  temperature?: number;
  oxygen_saturation?: number;
  weight?: number;
  height?: number;
  bmi?: number;
  pain_scale?: number;
  blood_glucose?: number;
}

const emptyVitals: VitalSigns = {};

const emptyForm = {
  patient_id: '',
  professional_id: '',
  appointment_id: '',
  evolution_date: new Date().toISOString().split('T')[0],
  evolution_time: new Date().toTimeString().slice(0, 5),
  evolution_entry_format: 'free_text' as 'soap' | 'free_text',
  evolution_free_text: '',
  subjective: '',
  objective: '',
  assessment: '',
  plan: '',
  vital_signs: { ...emptyVitals },
  diagnosis: '',
  diagnosis_code: '',
  procedures_performed: [] as any[],
  internal_notes: '',
  follow_up_notes: '',
  follow_up_date: '',
  metadata: {} as Record<string, any>,
};

const statusColors: Record<string, string> = {
  draft: '#f59e0b',
  signed: '#22c55e',
  amended: '#3b82f6',
  cancelled: '#ef4444',
};
const statusLabels: Record<string, string> = {
  draft: 'Rascunho',
  signed: 'Assinada',
  amended: 'Retificada',
  cancelled: 'Cancelada',
};

const toArray = (value: any): any[] => (Array.isArray(value) ? value : []);

const Evolutions: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTabletOrDown = useMediaQuery(theme.breakpoints.down('md'));
  const [evolutions, setEvolutions] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>({ total: 0, signed: 0, draft: 0 });

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterProfessional, setFilterProfessional] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [viewDialog, setViewDialog] = useState(false);
  const [viewData, setViewData] = useState<any>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState<any>({ ...emptyForm });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as any });

  const [patientSearch, setPatientSearch] = useState('');
  const [patientOptions, setPatientOptions] = useState<any[]>([]);
  const [profSearch, setProfSearch] = useState('');
  const [profOptions, setProfOptions] = useState<any[]>([]);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendDocData, setSendDocData] = useState<any>(null);

  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiPatientName, setAiPatientName] = useState('');
  const [aiComplaint, setAiComplaint] = useState('');
  const [aiMedicalHistory, setAiMedicalHistory] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState('');

  const [printWizardOpen, setPrintWizardOpen] = useState(false);
  const [indicationsDialogOpen, setIndicationsDialogOpen] = useState(false);
  const [attachedPrintables, setAttachedPrintables] = useState<EvolutionAttachedPrintable[]>([]);
  const [returnWorkflow, setReturnWorkflow] = useState<EvolutionReturnWorkflow>(() => defaultEvolutionReturnWorkflow());
  const [procedureLabels, setProcedureLabels] = useState<string[]>([]);

  const [searchParams, setSearchParams] = useSearchParams();
  const [filterPatientId, setFilterPatientId] = useState('');

  const lastPatientRef = useRef('');
  const lastProfRef = useRef('');
  const evolutionDeepLinkConsumedRef = useRef('');

  useEffect(() => {
    const p = searchParams.get('patient') || '';
    setFilterPatientId((prev) => (prev !== p ? p : prev));
  }, [searchParams]);

  useEffect(() => {
    loadEvolutions();
    loadStats();
  }, [search, filterStatus, filterProfessional, filterDateFrom, filterDateTo, filterPatientId, page, rowsPerPage]);

  useEffect(() => {
    if (openDialog) {
      searchPatients('');
      searchProfessionals('');
    }
  }, [openDialog]);

  useEffect(() => {
    if (patientSearch === lastPatientRef.current) return;
    const t = setTimeout(() => {
      if (openDialog) { lastPatientRef.current = patientSearch; searchPatients(patientSearch); }
    }, 300);
    return () => clearTimeout(t);
  }, [patientSearch, openDialog]);

  useEffect(() => {
    if (profSearch === lastProfRef.current) return;
    const t = setTimeout(() => {
      if (openDialog) { lastProfRef.current = profSearch; searchProfessionals(profSearch); }
    }, 300);
    return () => clearTimeout(t);
  }, [profSearch, openDialog]);

  const loadEvolutions = async () => {
    try {
      setLoading(true);
      const params: any = { page: page + 1, limit: rowsPerPage };
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      if (filterProfessional) params.professional_id = filterProfessional;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      if (filterPatientId.trim()) params.patient_id = filterPatientId.trim();
      const { data } = await axios.get(getApiUrl('/clinic/evolutions'), { params });
      setEvolutions(toArray(data?.data));
      setTotal(data.total || 0);
    } catch (err: any) {
      console.error(err);
      setEvolutions([]);
      setTotal(0);
      setSnackbar({
        open: true,
        message: err?.response?.data?.error || 'Erro ao carregar evoluções',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const { data } = await axios.get(getApiUrl('/clinic/evolutions/stats'));
      setStats(data);
    } catch { }
  };

  const searchPatients = async (q: string) => {
    try {
      const { data } = await axios.get(getApiUrl('/clinic/prescriptions-search/patients'), { params: { search: q } });
      setPatientOptions(toArray(data?.data ?? data));
    } catch {
      setPatientOptions([]);
    }
  };

  const searchProfessionals = async (q: string) => {
    try {
      const { data } = await axios.get(getApiUrl('/clinic/prescriptions-search/professionals'), { params: { search: q } });
      const rows = toArray(data?.data ?? data);
      setProfOptions(mergeLoggedInProfessionalIntoOptions(rows, user));
    } catch {
      const me = getLoggedInProfessionalOption(user);
      setProfOptions(me ? [me] : []);
    }
  };

  const handleNew = () => {
    setEditing(null);
    const me = getLoggedInProfessionalOption(user);
    setForm({
      ...emptyForm,
      evolution_date: new Date().toISOString().split('T')[0],
      evolution_time: new Date().toTimeString().slice(0, 5),
      ...(me ? { professional_id: me.id } : {}),
    });
    if (me) {
      setProfOptions([me]);
      setProfSearch(me.full_name);
    } else {
      setProfSearch('');
    }
    setAttachedPrintables([]);
    setReturnWorkflow(defaultEvolutionReturnWorkflow());
    setProcedureLabels([]);
    setOpenDialog(true);
  };

  const handleEdit = (evo: any) => {
    setEditing(evo);
    const mergedFreeText = (() => {
      const existing = String(evo.evolution_free_text ?? '').trim();
      if (existing) return evo.evolution_free_text ?? '';
      if (resolveEvolutionEntryFormat(evo) === 'free_text') return evo.evolution_free_text ?? '';
      const parts: string[] = [];
      if (evo.subjective?.trim()) parts.push(`Subjetivo\n${evo.subjective.trim()}`);
      if (evo.objective?.trim()) parts.push(`Objetivo\n${evo.objective.trim()}`);
      if (evo.assessment?.trim()) parts.push(`Avaliação\n${evo.assessment.trim()}`);
      if (evo.plan?.trim()) parts.push(`Plano\n${evo.plan.trim()}`);
      return parts.join('\n\n');
    })();

    setForm({
      ...emptyForm,
      ...evo,
      evolution_entry_format: 'free_text',
      evolution_free_text: mergedFreeText,
      subjective: '',
      objective: '',
      assessment: '',
      plan: '',
      vital_signs: evo.vital_signs || { ...emptyVitals },
      evolution_date: evo.evolution_date?.split('T')[0] || '',
      follow_up_date: evo.follow_up_date?.split('T')[0] || '',
      appointment_id: '',
    });
    if (evo.patient) {
      setPatientOptions([evo.patient]);
      setPatientSearch(evo.patient.full_name || '');
    }
    if (evo.professional) {
      setProfOptions([evo.professional]);
      setProfSearch(evo.professional.full_name || '');
    }
    const wf = evo.metadata?.evolution_workflow;
    if (wf?.printables?.length) setAttachedPrintables(wf.printables);
    else setAttachedPrintables([]);
    if (wf?.return) setReturnWorkflow({ ...defaultEvolutionReturnWorkflow(), ...wf.return });
    else setReturnWorkflow(defaultEvolutionReturnWorkflow());
    const pp = evo.procedures_performed || [];
    const labels = Array.isArray(pp) ? pp.map((p: any) => p?.name || p?.label).filter(Boolean) : [];
    setProcedureLabels(labels);
    setOpenDialog(true);
  };

  const handleSave = async () => {
    if (!form.patient_id || !form.professional_id) {
      setSnackbar({ open: true, message: 'Selecione paciente e profissional', severity: 'warning' });
      return;
    }
    try {
      setSaving(true);
      const payload = { ...form };
      delete payload.appointment_id;
      if (!payload.follow_up_date) delete payload.follow_up_date;

      const vs = payload.vital_signs || {};
      if (vs.weight && vs.height) {
        const h = vs.height / 100;
        vs.bmi = Math.round((vs.weight / (h * h)) * 10) / 10;
      }
      payload.vital_signs = vs;

      if (!editing) {
        const now = new Date();
        payload.evolution_date = now.toISOString().split('T')[0];
        payload.evolution_time = now.toTimeString().slice(0, 5);
      }

      payload.evolution_entry_format = 'free_text';
      payload.subjective = null;
      payload.objective = null;
      payload.assessment = null;
      payload.plan = null;

      payload.procedures_performed = procedureLabels.map((name) => ({ name, notes: '' }));
      payload.metadata = {
        ...(form.metadata || {}),
        evolution_workflow: {
          printables: attachedPrintables,
          return: returnWorkflow,
        },
      };

      if (editing) {
        await axios.put(getApiUrl(`/clinic/evolutions/${editing.id}`), payload);
        setSnackbar({ open: true, message: 'Evolução atualizada com sucesso', severity: 'success' });
      } else {
        await axios.post(getApiUrl('/clinic/evolutions'), payload);
        setSnackbar({ open: true, message: 'Evolução criada com sucesso', severity: 'success' });
      }
      setOpenDialog(false);
      loadEvolutions();
      loadStats();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Erro ao salvar evolução', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSign = async (id: string) => {
    if (!window.confirm('Deseja assinar esta evolução? Após assinada, não poderá ser excluída.')) return;
    try {
      await axios.patch(getApiUrl(`/clinic/evolutions/${id}/sign`));
      setSnackbar({ open: true, message: 'Evolução assinada com sucesso', severity: 'success' });
      loadEvolutions();
      loadStats();
      if (viewData?.id === id) handleView(id);
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Erro ao assinar', severity: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Deseja excluir esta evolução?')) return;
    try {
      await axios.delete(getApiUrl(`/clinic/evolutions/${id}`));
      setSnackbar({ open: true, message: 'Evolução excluída', severity: 'success' });
      loadEvolutions();
      loadStats();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Erro ao excluir', severity: 'error' });
    }
  };

  // ── AI Evolution ──
  const parseAiEvolution = (text: string) => {
    const result = { subjective: '', objective: '', assessment: '', plan: '', diagnosis: '', diagnosis_code: '', internal_notes: '', follow_up_notes: '' };
    const lines = text.split('\n').map(l => l.trim());
    let current: keyof typeof result | null = null;

    for (const line of lines) {
      const lower = line.toLowerCase();
      if (lower.startsWith('subjetivo:') || lower.startsWith('s:') || lower.startsWith('subjective:') || lower === 's' || lower === 'subjetivo') {
        current = 'subjective';
        const rest = line.split(':').slice(1).join(':').trim();
        if (rest) result.subjective = rest;
        continue;
      }
      if (lower.startsWith('objetivo:') || lower.startsWith('o:') || lower.startsWith('objective:') || lower === 'o' || lower === 'objetivo') {
        current = 'objective';
        const rest = line.split(':').slice(1).join(':').trim();
        if (rest) result.objective = rest;
        continue;
      }
      if (lower.startsWith('avaliação:') || lower.startsWith('avaliacao:') || lower.startsWith('a:') || lower.startsWith('assessment:') || lower === 'a' || lower === 'avaliação') {
        current = 'assessment';
        const rest = line.split(':').slice(1).join(':').trim();
        if (rest) result.assessment = rest;
        continue;
      }
      if (lower.startsWith('plano:') || lower.startsWith('p:') || lower.startsWith('plan:') || lower === 'p' || lower === 'plano') {
        current = 'plan';
        const rest = line.split(':').slice(1).join(':').trim();
        if (rest) result.plan = rest;
        continue;
      }
      if (lower.startsWith('diagnóstico:') || lower.startsWith('diagnostico:') || lower.startsWith('diagnosis:')) {
        result.diagnosis = line.split(':').slice(1).join(':').trim();
        current = null;
        continue;
      }
      if (lower.startsWith('cid:') || lower.startsWith('cid-10:') || lower.startsWith('código cid:')) {
        result.diagnosis_code = line.split(':').slice(1).join(':').trim().replace(/[^A-Z0-9.]/gi, '').substring(0, 20);
        current = null;
        continue;
      }
      if (lower.startsWith('observações:') || lower.startsWith('observacoes:') || lower.startsWith('notas:')) {
        current = 'internal_notes';
        const rest = line.split(':').slice(1).join(':').trim();
        if (rest) result.internal_notes = rest;
        continue;
      }
      if (lower.startsWith('retorno:') || lower.startsWith('follow-up:') || lower.startsWith('seguimento:')) {
        result.follow_up_notes = line.split(':').slice(1).join(':').trim();
        current = null;
        continue;
      }
      if (current && line) {
        result[current] += (result[current] ? '\n' : '') + line;
      }
    }
    return result;
  };

  const handleAiGenerate = async () => {
    if (!aiPatientName.trim() || !aiComplaint.trim()) {
      setAiError('Preencha o nome do paciente e a queixa principal');
      return;
    }
    setAiGenerating(true);
    setAiError('');
    try {
      const res = await axios.post(getApiUrl('/clinic/evolutions/ai-generate'), {
        patient_name: aiPatientName.trim(),
        complaint: aiComplaint.trim(),
        medical_history: aiMedicalHistory.trim(),
      });

      const aiData = res.data?.data;
      if (!aiData) throw new Error('Resposta vazia da IA');

      const rawText = typeof aiData === 'string' ? aiData : (aiData.output || aiData.text || aiData.response || aiData.evolution || JSON.stringify(aiData));
      const parsed = parseAiEvolution(rawText);

      const patientMatch = patientOptions.find((p: any) =>
        p.full_name?.toLowerCase().includes(aiPatientName.toLowerCase())
      );

      setEditing(null);
      setAttachedPrintables([]);
      setReturnWorkflow(defaultEvolutionReturnWorkflow());
      setProcedureLabels([]);
      setForm({
        ...emptyForm,
        evolution_entry_format: 'free_text',
        patient_id: patientMatch?.id || '',
        evolution_free_text: [
          parsed.subjective?.trim() && `Subjetivo\n${parsed.subjective.trim()}`,
          parsed.objective?.trim() && `Objetivo\n${parsed.objective.trim()}`,
          parsed.assessment?.trim() && `Avaliação\n${parsed.assessment.trim()}`,
          parsed.plan?.trim() && `Plano\n${parsed.plan.trim()}`,
        ]
          .filter(Boolean)
          .join('\n\n'),
        diagnosis: parsed.diagnosis,
        internal_notes: parsed.internal_notes,
        follow_up_notes: parsed.follow_up_notes,
      });

      if (patientMatch) {
        setPatientOptions([patientMatch]);
        setPatientSearch(patientMatch.full_name || '');
      }

      setAiDialogOpen(false);
      setOpenDialog(true);
      setSnackbar({ open: true, message: 'Evolução gerada com IA! Revise os campos antes de concluir.', severity: 'success' });
    } catch (err: any) {
      setAiError(err.response?.data?.error || err.message || 'Erro ao gerar evolução com IA');
    } finally {
      setAiGenerating(false);
    }
  };

  const openEvolutionView = useCallback(async (id: string) => {
    try {
      setViewLoading(true);
      setViewDialog(true);
      const { data } = await axios.get(getApiUrl(`/clinic/evolutions/${id}`));
      setViewData(data);
    } catch (err: any) {
      console.error(err);
      setSnackbar({ open: true, message: 'Erro ao carregar evolução', severity: 'error' });
      setViewDialog(false);
    } finally {
      setViewLoading(false);
    }
  }, []);

  const handleView = (id: string) => {
    void openEvolutionView(id);
  };

  useEffect(() => {
    const evoFromUrl = searchParams.get('evo') || searchParams.get('evolution') || '';
    if (!evoFromUrl) {
      evolutionDeepLinkConsumedRef.current = '';
      return;
    }

    const key = `evo:${evoFromUrl}`;
    if (evolutionDeepLinkConsumedRef.current === key) return;
    evolutionDeepLinkConsumedRef.current = key;

    void (async () => {
      await openEvolutionView(evoFromUrl);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('evo');
          next.delete('evolution');
          return next;
        },
        { replace: true },
      );
    })();
  }, [searchParams, setSearchParams, openEvolutionView]);

  const clearPatientUrlFilter = () => {
    setFilterPatientId('');
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('patient');
        return next;
      },
      { replace: true },
    );
    setPage(0);
  };

  const printHtmlInNewWindow = (html: string) => {
    const w = window.open('', '_blank');
    if (w) {
      w.document.write(html);
      w.document.close();
      setTimeout(() => w.print(), 400);
    }
  };

  const extractBodyInner = (fullHtml: string) => {
    const m = fullHtml.match(/<body[^>]*>([\s\S]*)<\/body>/i);
    return m ? m[1].trim() : fullHtml;
  };

  const handlePrint = async (evo: any) => {
    await printClinicalEvolutionFullDocument(evo);
  };
  const updateVital = (field: string, value: string) => {
    const num = value === '' ? undefined : parseFloat(value);
    setForm((prev: any) => ({ ...prev, vital_signs: { ...prev.vital_signs, [field]: num } }));
  };

  const printableCategoryLabel: Record<EvolutionPrintableCategory, string> = {
    prescription: 'Receituário',
    certificate: 'Atestado',
    exam_order: 'Exames',
    indication: 'Indicações',
    report: 'Laudo',
    surgical_risk: 'Risco cirúrgico',
  };

  const printAttachedDocuments = async (items: EvolutionAttachedPrintable[]) => {
    if (!items.length) return;
    let clinic: any = null;
    try {
      const { data } = await axios.get(getApiUrl('/clinic/settings'));
      clinic = data?.data ?? null;
    } catch {
      /* ignore */
    }

    const parts: string[] = [];
    for (const p of items) {
      if (p.linkedDocumentId && p.linkedDocumentType === 'prescription') {
        try {
          const { data } = await axios.get(getApiUrl(`/clinic/prescriptions/${p.linkedDocumentId}/print`));
          const printData = data?.data ?? data;
          parts.push(
            `<div class="attachment-block attachment-block--embedded">${extractBodyInner(buildPrescriptionPrintHtml(printData))}</div>`,
          );
        } catch {
          const body = sanitizeClinicalHtml(String(p.bodyText || '').replace(/\r\n|\r|\n/g, '<br/>'));
          const cat = printableCategoryLabel[p.category] || p.category;
          parts.push(buildEvolutionPrintAttachmentSection(cat, p.templateName, body));
        }
      } else if (p.linkedDocumentId && p.linkedDocumentType === 'certificate') {
        try {
          const { data } = await axios.get(getApiUrl(`/clinic/certificates/${p.linkedDocumentId}/print`));
          const printData = data?.data ?? data;
          parts.push(
            `<div class="attachment-block attachment-block--embedded">${extractBodyInner(buildCertificatePrintHtml(printData))}</div>`,
          );
        } catch {
          const body = sanitizeClinicalHtml(String(p.bodyText || '').replace(/\r\n|\r|\n/g, '<br/>'));
          const cat = printableCategoryLabel[p.category] || p.category;
          parts.push(buildEvolutionPrintAttachmentSection(cat, p.templateName, body));
        }
      } else {
        const body = sanitizeClinicalHtml(String(p.bodyText || '').replace(/\r\n|\r|\n/g, '<br/>'));
        const cat = printableCategoryLabel[p.category] || p.category;
        parts.push(buildEvolutionPrintAttachmentSection(cat, p.templateName, body));
      }
    }

    const sheets = parts.map((bodyHtml) => ({ bodyHtml }));
    printHtmlInNewWindow(
      buildClinicMultiSheetPrintHtml({
        clinic,
        browserTitle: 'Impressão',
        sheets,
      }),
    );
  };

  const printProcedureLabel = async (name: string) => {
    let clinic: any = null;
    try {
      const { data } = await axios.get(getApiUrl('/clinic/settings'));
      clinic = data?.data ?? null;
    } catch {
      /* ignore */
    }
    const section = buildEvolutionPrintAttachmentSection(
      'Indicações',
      'Procedimento indicado',
      `<p style="margin:0">${escapeHtml(name)}</p>`,
    );
    printHtmlInNewWindow(
      buildClinicPrintDocumentHtml({
        clinic,
        browserTitle: name.slice(0, 120),
        badgeText: 'Indicação',
        bodyHtml: section,
        footerNote: 'Documento emitido pelo sistema da clínica.',
      }),
    );
  };

  const kpis = [
    { label: 'Total', value: stats.total, color: '#3b82f6', icon: <DescIcon /> },
    { label: 'Assinadas', value: stats.signed, color: '#22c55e', icon: <SignedIcon /> },
    { label: 'Rascunhos', value: stats.draft, color: '#f59e0b', icon: <DraftIcon /> },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, backgroundColor: 'transparent', minHeight: '100vh', pb: { xs: 10, sm: 3 } }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        mb: { xs: 2, md: 3 },
        gap: 2,
      }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 800, background: 'linear-gradient(135deg, #14B8A6 0%, #0f766e 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', lineHeight: 1.2 }}>
            Evoluções Clínicas
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>Registro em texto livre com impressos e retorno</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}>
          <Button variant="outlined" fullWidth={isMobile} onClick={() => { loadEvolutions(); loadStats(); }} startIcon={<RefreshIcon />} sx={{ borderColor: '#e2e8f0', color: '#64748b', textTransform: 'none' }}>
            Atualizar
          </Button>
          <Button variant="contained" fullWidth={isMobile} startIcon={<AiIcon />}
            onClick={() => { setAiPatientName(''); setAiComplaint(''); setAiMedicalHistory(''); setAiError(''); searchPatients(''); setAiDialogOpen(true); }}
            sx={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)', '&:hover': { background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)' }, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
            Gerar com IA
          </Button>
          <Button variant="contained" fullWidth={isMobile} startIcon={<AddIcon />} onClick={handleNew}
            sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' }, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
            Nova Evolução
          </Button>
        </Box>
      </Box>

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {kpis.map((kpi) => (
          <Grid item xs={4} key={kpi.label}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2, '&:last-child': { pb: 2 } }}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 500 }}>{kpi.label}</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: kpi.color }}>{kpi.value}</Typography>
                </Box>
                {!isMobile && <Box sx={{ color: kpi.color, opacity: 0.3 }}>{kpi.icon}</Box>}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {filterPatientId.trim() !== '' && (
        <Alert
          severity="info"
          sx={{ mb: 2, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={clearPatientUrlFilter} sx={{ textTransform: 'none' }}>
              Limpar filtro
            </Button>
          }
        >
          Lista filtrada por paciente (via link). Mostrando apenas as evoluções desse paciente.
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: { xs: 2, md: 3 }, borderRadius: 3 }}>
        <CardContent sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          flexWrap: 'wrap',
          p: 2,
          '&:last-child': { pb: 2 },
        }}>
          <TextField size="small" fullWidth placeholder="Buscar na evolução..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            sx={{ minWidth: { sm: 220 }, flex: { sm: 1 } }} />
          <FormControl size="small" fullWidth={isMobile} sx={{ minWidth: { sm: 140 } }}>
            <InputLabel>Status</InputLabel>
            <Select value={filterStatus} label="Status" onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}>
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(statusLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" type="date" label="De" value={filterDateFrom} onChange={(e) => { setFilterDateFrom(e.target.value); setPage(0); }} InputLabelProps={{ shrink: true }} sx={{ width: { xs: '100%', sm: 150 } }} />
          <TextField size="small" type="date" label="Até" value={filterDateTo} onChange={(e) => { setFilterDateTo(e.target.value); setPage(0); }} InputLabelProps={{ shrink: true }} sx={{ width: { xs: '100%', sm: 150 } }} />
        </CardContent>
      </Card>

      {/* Lista: cards (mobile/tablet) | tabela (desktop) */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: '#14B8A6' }} /></Box>
      ) : isTabletOrDown ? (
        <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Stack spacing={1.5} sx={{ p: 1.5 }}>
            {evolutions.length === 0 ? (
              <Paper sx={{ py: 6, textAlign: 'center', borderRadius: 2, border: '1px dashed #e2e8f0' }}>
                <Typography sx={{ color: '#94a3b8' }}>Nenhuma evolução encontrada</Typography>
              </Paper>
            ) : evolutions.map((evo) => (
              <Card
                key={evo.id}
                elevation={0}
                onClick={() => handleView(evo.id)}
                sx={{
                  borderRadius: 2,
                  border: '1px solid #e2e8f0',
                  cursor: 'pointer',
                  '&:active': { transform: 'scale(0.99)' },
                }}
              >
                <CardContent sx={{ py: 1.75, px: 2, '&:last-child': { pb: 1.75 } }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <Avatar sx={{ width: 44, height: 44, bgcolor: '#14B8A620', color: '#14B8A6', fontSize: 16, flexShrink: 0 }}>
                      {evo.patient?.full_name?.[0] || 'P'}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }}>
                        {evo.patient?.full_name || '—'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>
                        {evo.evolution_date ? new Date(evo.evolution_date + 'T12:00:00').toLocaleDateString('pt-BR') : '-'}
                        {evo.evolution_time ? ` · ${evo.evolution_time.slice(0, 5)}` : ''}
                      </Typography>
                      {evo.professional?.full_name && (
                        <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 0.25 }}>{evo.professional.full_name}</Typography>
                      )}
                      {evo.diagnosis && (
                        <Typography variant="body2" sx={{ color: '#475569', mt: 0.75, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                          {evo.diagnosis_code ? `${evo.diagnosis_code} · ` : ''}{evo.diagnosis}
                        </Typography>
                      )}
                      <Box sx={{ mt: 1 }}>
                        <Chip label={statusLabels[evo.status] || evo.status} size="small"
                          sx={{ bgcolor: (statusColors[evo.status] || '#666') + '15', color: statusColors[evo.status] || '#666', fontWeight: 600, fontSize: '0.75rem' }} />
                      </Box>
                    </Box>
                  </Box>
                  <Divider sx={{ my: 1.25 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Visualizar"><IconButton size="medium" onClick={() => handleView(evo.id)} sx={{ color: '#14B8A6' }}><ViewIcon /></IconButton></Tooltip>
                    <Tooltip title="Imprimir"><IconButton size="medium" onClick={() => void handlePrint(evo)} sx={{ color: '#3b82f6' }}><PrintIcon /></IconButton></Tooltip>
                    <Tooltip title="Enviar"><IconButton size="medium" onClick={() => { setSendDocData({ id: evo.id, patientId: evo.patient_id, email: evo.patient?.email, phone: evo.patient?.phone, whatsapp: evo.patient?.whatsapp, name: evo.patient?.full_name }); setSendDialogOpen(true); }} sx={{ color: '#3b82f6' }}><ShareIcon /></IconButton></Tooltip>
                    {evo.status === 'draft' && (
                      <>
                        <Tooltip title="Editar"><IconButton size="medium" onClick={() => handleEdit(evo)} sx={{ color: '#f59e0b' }}><EditIcon /></IconButton></Tooltip>
                        <Tooltip title="Excluir"><IconButton size="medium" onClick={() => handleDelete(evo.id)} sx={{ color: '#ef4444' }}><DeleteIcon /></IconButton></Tooltip>
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
          <TablePagination component="div" count={total} page={page} rowsPerPage={rowsPerPage}
            onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 20, 50]} labelRowsPerPage="Por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{ borderTop: '1px solid #e2e8f0', px: 1, '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', gap: 1 } }} />
        </Card>
      ) : (
        <Card sx={{ borderRadius: 3 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Data</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Paciente</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Profissional</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Diagnóstico</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {evolutions.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: '#94a3b8' }}>Nenhuma evolução encontrada</TableCell></TableRow>
                ) : evolutions.map((evo) => (
                  <TableRow key={evo.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleView(evo.id)}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {evo.evolution_date ? new Date(evo.evolution_date + 'T12:00:00').toLocaleDateString('pt-BR') : '-'}
                      </Typography>
                      {evo.evolution_time && <Typography variant="caption" sx={{ color: '#94a3b8' }}>{evo.evolution_time.slice(0, 5)}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: '#14B8A620', color: '#14B8A6', fontSize: 14 }}>
                          {evo.patient?.full_name?.[0] || 'P'}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{evo.patient?.full_name || '-'}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: '#64748b' }}>{evo.professional?.full_name || '-'}</TableCell>
                    <TableCell>
                      {evo.diagnosis ? (
                        <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {evo.diagnosis_code && <Chip label={evo.diagnosis_code} size="small" sx={{ mr: 0.5, fontSize: '0.7rem', height: 20, bgcolor: '#ede9fe', color: '#7c3aed' }} />}
                          {evo.diagnosis}
                        </Typography>
                      ) : <Typography variant="body2" sx={{ color: '#cbd5e1' }}>—</Typography>}
                    </TableCell>
                    <TableCell>
                      <Chip label={statusLabels[evo.status] || evo.status} size="small"
                        sx={{ bgcolor: (statusColors[evo.status] || '#666') + '15', color: statusColors[evo.status] || '#666', fontWeight: 600, fontSize: '0.75rem' }} />
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Visualizar"><IconButton size="small" onClick={() => handleView(evo.id)} sx={{ color: '#14B8A6' }}><ViewIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Imprimir"><IconButton size="small" onClick={() => void handlePrint(evo)} sx={{ color: '#3b82f6' }}><PrintIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Enviar"><IconButton size="small" onClick={() => { setSendDocData({ id: evo.id, patientId: evo.patient_id, email: evo.patient?.email, phone: evo.patient?.phone, whatsapp: evo.patient?.whatsapp, name: evo.patient?.full_name }); setSendDialogOpen(true); }} sx={{ color: '#3b82f6' }}><ShareIcon fontSize="small" /></IconButton></Tooltip>
                      {evo.status === 'draft' && (
                        <>
                          <Tooltip title="Editar"><IconButton size="small" onClick={() => handleEdit(evo)} sx={{ color: '#f59e0b' }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title="Excluir"><IconButton size="small" onClick={() => handleDelete(evo.id)} sx={{ color: '#ef4444' }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={total} page={page} rowsPerPage={rowsPerPage}
            onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 20, 50]} labelRowsPerPage="Por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`} />
        </Card>
      )}

      {/* ===== Create/Edit Dialog ===== */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        PaperProps={{
          sx: isMobile
            ? { m: 0, maxHeight: '100%', borderRadius: 0, height: '100%', display: 'flex', flexDirection: 'column' }
            : { borderRadius: 3, maxHeight: '95vh' },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1, flexShrink: 0, px: { xs: 2, sm: 3 } }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {editing ? 'Editar evolução' : 'Nova evolução clínica'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              Texto clínico, impressos, indicações e retorno em um único fluxo
            </Typography>
          </Box>
          <IconButton onClick={() => setOpenDialog(false)} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />

        <DialogContent sx={{ pt: 2, flex: 1, overflow: 'auto', px: { xs: 2, sm: 3 }, pb: 1 }}>
          <Accordion defaultExpanded sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' }, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'divider' }}>
            <AccordionSummary expandIcon={<ExpandMoreAccordionIcon />} sx={{ px: 2, minHeight: 48, '& .MuiAccordionSummary-content': { my: 1 } }}>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>Identificação do atendimento</Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Paciente e profissional responsável
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    size="small"
                    options={patientOptions}
                    getOptionLabel={(o: any) => `${o.full_name || ''}${o.cpf ? ` - ${o.cpf}` : ''}`}
                    onInputChange={(_, v, r) => {
                      if (r !== 'reset') setPatientSearch(v);
                    }}
                    onChange={(_, v) => setForm({ ...form, patient_id: v?.id || '' })}
                    value={toArray(patientOptions).find((p: any) => p.id === form.patient_id) || null}
                    renderInput={(params) => <TextField {...params} label="Paciente *" />}
                    isOptionEqualToValue={(o: any, v: any) => o.id === v.id}
                    noOptionsText="Nenhum paciente encontrado"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    size="small"
                    options={profOptions}
                    getOptionLabel={(o: any) => `${o.full_name || ''}${o.specialty ? ` - ${o.specialty}` : ''}`}
                    onInputChange={(_, v, r) => {
                      if (r !== 'reset') setProfSearch(v);
                    }}
                    onChange={(_, v) => setForm({ ...form, professional_id: v?.id || '' })}
                    value={toArray(profOptions).find((p: any) => p.id === form.professional_id) || null}
                    renderInput={(params) => <TextField {...params} label="Profissional *" />}
                    isOptionEqualToValue={(o: any, v: any) => o.id === v.id}
                    noOptionsText="Nenhum profissional encontrado"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Diagnóstico"
                    value={form.diagnosis}
                    onChange={(e) => setForm({ ...form, diagnosis: e.target.value })}
                    placeholder="Opcional — ex.: catarata nuclear"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Paper
            elevation={0}
            sx={{
              mb: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.25,
                bgcolor: '#f0fdfa',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="overline"
                sx={{ fontWeight: 800, color: '#0f766e', letterSpacing: '0.12em' }}
              >
                Evolução
              </Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              <TextField
                fullWidth
                multiline
                minRows={12}
                maxRows={32}
                value={form.evolution_free_text}
                onChange={(e) => setForm({ ...form, evolution_free_text: e.target.value })}
                placeholder="Evolução em texto livre padrão..."
                size="small"
              />
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              mb: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Box
              onClick={() => setPrintWizardOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setPrintWizardOpen(true);
                }
              }}
              role="button"
              tabIndex={0}
              sx={{
                px: 2,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                cursor: 'pointer',
                bgcolor: '#fafafa',
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:hover': { bgcolor: '#f1f5f9' },
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '0.8rem', letterSpacing: '0.06em', color: '#0f172a' }}>
                  IMPRESSOS E SOLICITAÇÕES
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Receituário, atestado, pedido de exame, laudo ou risco cirúrgico (para cirurgias e procedimentos, use a área Indicações logo abaixo)
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={<NoteAddIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  setPrintWizardOpen(true);
                }}
                sx={{ textTransform: 'none', flexShrink: 0, borderColor: '#cbd5e1', color: '#334155' }}
              >
                Adicionar
              </Button>
            </Box>
            <Box sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                Materiais selecionados
              </Typography>
              <Box
                sx={{
                  mt: 1.25,
                  minHeight: 48,
                  maxHeight: 168,
                  overflow: 'auto',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  alignContent: 'flex-start',
                }}
              >
                {attachedPrintables.length === 0 ? (
                  <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                    Nenhum documento anexado. Use o cabeçalho acima para lançar modelos já cadastrados na clínica.
                  </Typography>
                ) : (
                  attachedPrintables.map((p) => (
                    <Paper
                      key={p.clientKey}
                      variant="outlined"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.25,
                        pl: 1.25,
                        pr: 0.25,
                        py: 0.25,
                        borderRadius: 10,
                        borderColor: '#e2e8f0',
                        bgcolor: '#fff',
                      }}
                    >
                      <Typography variant="body2" noWrap sx={{ maxWidth: { xs: 160, sm: 240 }, fontWeight: 600 }}>
                        {p.category === 'prescription' ? evolutionPrintableListLabel(p) : p.templateName}
                      </Typography>
                      {p.category !== 'prescription' ? (
                      <Typography variant="caption" sx={{ color: '#94a3b8', display: { xs: 'none', sm: 'inline' }, mr: 0.5 }}>
                        {printableCategoryLabel[p.category]}
                      </Typography>
                      ) : null}
                      <Tooltip title="Imprimir este documento">
                        <IconButton size="small" onClick={() => void printAttachedDocuments([p])} aria-label="Imprimir">
                          <PrintIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remover">
                        <IconButton
                          size="small"
                          onClick={() =>
                            setAttachedPrintables((prev) => prev.filter((x) => x.clientKey !== p.clientKey))
                          }
                          aria-label="Remover"
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Paper>
                  ))
                )}
              </Box>
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              mb: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Box
              onClick={() => setIndicationsDialogOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIndicationsDialogOpen(true);
                }
              }}
              role="button"
              tabIndex={0}
              sx={{
                px: 2,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                cursor: 'pointer',
                bgcolor: '#fafafa',
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:hover': { bgcolor: '#f1f5f9' },
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: '0.8rem', letterSpacing: '0.06em', color: '#0f172a' }}>
                  INDICAÇÕES
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  Cirurgias e procedimentos. Use Adicionar para abrir o seletor (cadastro ou grade OD/OE/AO em oftalmologia).
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={<NoteAddIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  setIndicationsDialogOpen(true);
                }}
                sx={{ textTransform: 'none', flexShrink: 0, borderColor: '#cbd5e1', color: '#334155' }}
              >
                Adicionar
              </Button>
            </Box>
            <Box sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                Cirurgias e procedimentos selecionados
              </Typography>
              <Box
                sx={{
                  mt: 1.25,
                  minHeight: 44,
                  maxHeight: 160,
                  overflow: 'auto',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                {procedureLabels.length === 0 ? (
                  <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                    Nenhuma indicação registrada.
                  </Typography>
                ) : (
                  procedureLabels.map((label, idx) => (
                    <Paper
                      key={`proc-${idx}-${label}`}
                      variant="outlined"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.25,
                        pl: 1.25,
                        pr: 0.25,
                        py: 0.25,
                        borderRadius: 10,
                        borderColor: '#e2e8f0',
                      }}
                    >
                      <Typography variant="body2" noWrap sx={{ maxWidth: { xs: 180, sm: 280 }, fontWeight: 600 }}>
                        {label}
                      </Typography>
                      <Tooltip title="Imprimir indicação">
                        <IconButton size="small" onClick={() => void printProcedureLabel(label)} aria-label="Imprimir">
                          <PrintIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remover">
                        <IconButton
                          size="small"
                          onClick={() =>
                            setProcedureLabels((prev) => prev.filter((x) => x !== label))
                          }
                          aria-label="Remover"
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Paper>
                  ))
                )}
              </Box>
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              mb: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ px: 2, py: 1.25, bgcolor: '#f0fdfa', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="overline" sx={{ fontWeight: 800, color: '#0f766e', letterSpacing: '0.12em' }}>
                Retorno
              </Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={returnWorkflow.returnWithExams}
                      onChange={(e) =>
                        setReturnWorkflow((w) => ({ ...w, returnWithExams: e.target.checked }))
                      }
                      sx={{ color: '#14B8A6', '&.Mui-checked': { color: '#14B8A6' } }}
                    />
                  }
                  label="Retorno com exames"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={returnWorkflow.returnForSurgery}
                      onChange={(e) =>
                        setReturnWorkflow((w) => ({ ...w, returnForSurgery: e.target.checked }))
                      }
                      sx={{ color: '#14B8A6', '&.Mui-checked': { color: '#14B8A6' } }}
                    />
                  }
                  label="Retorno para cirurgia"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={returnWorkflow.dischargeNoRecall}
                      onChange={(e) =>
                        setReturnWorkflow((w) => ({ ...w, dischargeNoRecall: e.target.checked }))
                      }
                      sx={{ color: '#14B8A6', '&.Mui-checked': { color: '#14B8A6' } }}
                    />
                  }
                  label={'Alta — não reconvocar'}
                />
              </FormGroup>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Retorno em (dias)"
                    type="number"
                    value={returnWorkflow.returnInDays}
                    onChange={(e) =>
                      setReturnWorkflow((w) => ({ ...w, returnInDays: e.target.value }))
                    }
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Reavaliação em (dias)"
                    type="number"
                    value={returnWorkflow.reevaluationInDays}
                    onChange={(e) =>
                      setReturnWorkflow((w) => ({ ...w, reevaluationInDays: e.target.value }))
                    }
                    inputProps={{ min: 0 }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Paper>

          <Accordion
            defaultExpanded={false}
            sx={{ mb: 1, borderRadius: 2, '&:before': { display: 'none' }, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'divider' }}
          >
            <AccordionSummary expandIcon={<ExpandMoreAccordionIcon />} sx={{ px: 2 }}>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>Sinais vitais e complementar</Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  PA, FC, peso, notas internas e retorno em texto livre
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                Preencha apenas o que foi aferido. IMC calculado quando peso e altura existirem.
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="PA sistólica (mmHg)"
                    type="number"
                    value={form.vital_signs?.blood_pressure_systolic || ''}
                    onChange={(e) => updateVital('blood_pressure_systolic', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <HeartIcon sx={{ color: '#ef4444', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="PA diastólica (mmHg)"
                    type="number"
                    value={form.vital_signs?.blood_pressure_diastolic || ''}
                    onChange={(e) => updateVital('blood_pressure_diastolic', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <HeartIcon sx={{ color: '#ef4444', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="FC (bpm)"
                    type="number"
                    value={form.vital_signs?.heart_rate || ''}
                    onChange={(e) => updateVital('heart_rate', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SpeedIcon sx={{ color: '#f59e0b', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="FR (irpm)"
                    type="number"
                    value={form.vital_signs?.respiratory_rate || ''}
                    onChange={(e) => updateVital('respiratory_rate', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LungIcon sx={{ color: '#3b82f6', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Temperatura (°C)"
                    type="number"
                    value={form.vital_signs?.temperature || ''}
                    onChange={(e) => updateVital('temperature', e.target.value)}
                    inputProps={{ step: 0.1 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <TempIcon sx={{ color: '#ef4444', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="SpO₂ (%)"
                    type="number"
                    value={form.vital_signs?.oxygen_saturation || ''}
                    onChange={(e) => updateVital('oxygen_saturation', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BloodIcon sx={{ color: '#8b5cf6', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 0.5 }} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Peso (kg)"
                    type="number"
                    value={form.vital_signs?.weight || ''}
                    onChange={(e) => updateVital('weight', e.target.value)}
                    inputProps={{ step: 0.1 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <WeightIcon sx={{ color: '#14B8A6', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Altura (cm)"
                    type="number"
                    value={form.vital_signs?.height || ''}
                    onChange={(e) => updateVital('height', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <HeightIcon sx={{ color: '#14B8A6', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="IMC"
                    type="number"
                    disabled
                    value={
                      form.vital_signs?.weight && form.vital_signs?.height
                        ? (
                            form.vital_signs.weight /
                            Math.pow(form.vital_signs.height / 100, 2)
                          ).toFixed(1)
                        : ''
                    }
                    helperText={(() => {
                      if (!form.vital_signs?.weight || !form.vital_signs?.height) {
                        return 'Calculado automaticamente';
                      }
                      const bmi = form.vital_signs.weight / Math.pow(form.vital_signs.height / 100, 2);
                      if (bmi < 18.5) return 'Abaixo do peso';
                      if (bmi < 25) return 'Peso normal';
                      if (bmi < 30) return 'Sobrepeso';
                      return 'Obesidade';
                    })()}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Glicemia (mg/dL)"
                    type="number"
                    value={form.vital_signs?.blood_glucose || ''}
                    onChange={(e) => updateVital('blood_glucose', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Escala de dor (0–10)"
                    type="number"
                    value={form.vital_signs?.pain_scale ?? ''}
                    onChange={(e) => updateVital('pain_scale', e.target.value)}
                    inputProps={{ min: 0, max: 10 }}
                    helperText={(() => {
                      const p = form.vital_signs?.pain_scale;
                      if (p === undefined || p === null || p === '') return '';
                      if (p <= 3) return 'Dor leve';
                      if (p <= 6) return 'Dor moderada';
                      return 'Dor intensa';
                    })()}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    maxRows={10}
                    label="Observações internas"
                    value={form.internal_notes}
                    onChange={(e) => setForm({ ...form, internal_notes: e.target.value })}
                    placeholder="Notas para a equipe (não destinadas ao paciente)"
                    helperText="Visível apenas na clínica"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    maxRows={10}
                    label="Orientações de retorno (texto livre)"
                    value={form.follow_up_notes}
                    onChange={(e) => setForm({ ...form, follow_up_notes: e.target.value })}
                    placeholder="Combine com as opções de retorno acima, se desejar"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Data prevista de retorno"
                    value={form.follow_up_date}
                    onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            flexShrink: 0,
            flexWrap: 'wrap',
            gap: 2,
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: '#fafafa',
          }}
        >
          <Box
            sx={{
              px: 1.5,
              py: 0.75,
              borderRadius: 2,
              bgcolor: '#fffbeb',
              border: '1px solid #fcd34d',
              maxWidth: { xs: '100%', sm: 360 },
            }}
          >
            <Typography variant="caption" sx={{ color: '#92400e', fontWeight: 600 }}>
              Obs.: salvamento ao concluir — as alterações são gravadas no prontuário quando você usa Concluir.
            </Typography>
          </Box>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            justifyContent="flex-end"
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            <Button
              onClick={() => setOpenDialog(false)}
              sx={{ color: '#64748b', textTransform: 'none' }}
            >
              Cancelar
            </Button>
            <Button
              variant="outlined"
              disabled={!evolutionHasPrintBatchContent(attachedPrintables, procedureLabels)}
              startIcon={<PrintIcon />}
              onClick={() =>
                void printAttachedDocuments(buildEvolutionPrintBatch(attachedPrintables, procedureLabels))
              }
              sx={{ textTransform: 'none', borderColor: '#cbd5e1', color: '#334155' }}
            >
              Imprimir todos
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                minWidth: 140,
                fontWeight: 800,
                letterSpacing: 0.06,
                textTransform: 'none',
                bgcolor: '#14B8A6',
                '&:hover': { bgcolor: '#0d9488' },
              }}
            >
              {saving ? 'Salvando…' : 'Concluir'}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      {/* ===== View Dialog ===== */}
      <Dialog
        open={viewDialog}
        onClose={() => setViewDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        PaperProps={{
          sx: isMobile
            ? { m: 0, borderRadius: 0, height: '100%', maxHeight: '100%', display: 'flex', flexDirection: 'column' }
            : { borderRadius: 3 },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, px: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HospitalIcon sx={{ color: '#14B8A6' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Evolução Clínica</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {viewData?.status === 'draft' && (
              <>
                <Tooltip title="Assinar"><IconButton onClick={() => handleSign(viewData.id)} sx={{ color: '#22c55e' }}><SignedIcon /></IconButton></Tooltip>
                <Tooltip title="Editar"><IconButton onClick={() => { setViewDialog(false); handleEdit(viewData); }} sx={{ color: '#f59e0b' }}><EditIcon /></IconButton></Tooltip>
              </>
            )}
            <Tooltip title="Imprimir documento completo (resumo da evolução)">
              <IconButton onClick={() => void handlePrint(viewData)} sx={{ color: '#3b82f6' }} aria-label="Imprimir documento completo">
                <PrintIcon />
              </IconButton>
            </Tooltip>
            {viewData ? (
              <Tooltip title="Imprimir anexos (cada impresso e indicações em lote com separação)">
                <span>
                  <IconButton
                    size="small"
                    disabled={
                      !evolutionHasPrintBatchContent(
                        getEvolutionWorkflowPrintables(viewData),
                        normalizeEvolutionProcedureLabels(viewData),
                      )
                    }
                    onClick={() =>
                      void printAttachedDocuments(
                        buildEvolutionPrintBatch(
                          getEvolutionWorkflowPrintables(viewData),
                          normalizeEvolutionProcedureLabels(viewData),
                        ),
                      )
                    }
                    sx={{ color: '#7c3aed' }}
                    aria-label="Imprimir anexos da evolução"
                  >
                    <LayersIcon />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            <Tooltip title="Enviar"><IconButton onClick={() => { setSendDocData({ id: viewData?.id, patientId: viewData?.patient_id, email: viewData?.patient?.email, phone: viewData?.patient?.phone, whatsapp: viewData?.patient?.whatsapp, name: viewData?.patient?.full_name }); setSendDialogOpen(true); }} sx={{ color: '#3b82f6' }}><ShareIcon /></IconButton></Tooltip>
            <IconButton onClick={() => setViewDialog(false)} aria-label="Fechar"><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent dividers sx={{ pt: 2, flex: 1, overflow: 'auto', px: { xs: 2, sm: 3 } }}>
          {viewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : viewData ? (
            <Box>
              {/* Patient & Professional */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Card sx={{ flex: 1, minWidth: 250, borderRadius: 2, bgcolor: '#f0fdfa', border: '1px solid #99f6e4' }}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" sx={{ color: '#0f766e', fontWeight: 600 }}>PACIENTE</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{viewData.patient?.full_name || '-'}</Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      {viewData.patient?.cpf || ''} {viewData.patient?.birth_date ? ` | Nasc: ${new Date(viewData.patient.birth_date + 'T12:00:00').toLocaleDateString('pt-BR')}` : ''}
                    </Typography>
                  </CardContent>
                </Card>
                <Card sx={{ flex: 1, minWidth: 250, borderRadius: 2, bgcolor: '#f0f9ff', border: '1px solid #bae6fd' }}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="caption" sx={{ color: '#0369a1', fontWeight: 600 }}>PROFISSIONAL</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{viewData.professional?.full_name || '-'}</Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      {viewData.professional?.specialty || ''} | {viewData.professional?.council_type || 'CRM'} {viewData.professional?.council_number || ''}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              {/* Date & Status */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Chip icon={<CalendarIcon />}
                  label={`${viewData.evolution_date ? new Date(viewData.evolution_date + 'T12:00:00').toLocaleDateString('pt-BR') : '-'}${viewData.evolution_time ? ' às ' + viewData.evolution_time.slice(0, 5) : ''}`}
                  sx={{ fontWeight: 600 }} />
                <Chip label={statusLabels[viewData.status] || viewData.status}
                  sx={{ bgcolor: (statusColors[viewData.status] || '#666') + '20', color: statusColors[viewData.status] || '#666', fontWeight: 700 }} />
                {viewData.is_signed && <Chip icon={<SignedIcon />} label="Assinada digitalmente" color="success" variant="outlined" size="small" />}
              </Box>

              {/* Vital Signs */}
              {viewData.vital_signs && Object.values(viewData.vital_signs).some((v: any) => v) && (
                <Card sx={{ mb: 2, borderRadius: 2, border: '1px solid #e2e8f0' }}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f766e', mb: 1 }}>Sinais Vitais</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                      {viewData.vital_signs.blood_pressure_systolic && (
                        <VitalChip icon={<HeartIcon />} label="PA" value={`${viewData.vital_signs.blood_pressure_systolic}/${viewData.vital_signs.blood_pressure_diastolic || '?'} mmHg`} color="#ef4444" />
                      )}
                      {viewData.vital_signs.heart_rate && <VitalChip icon={<SpeedIcon />} label="FC" value={`${viewData.vital_signs.heart_rate} bpm`} color="#f59e0b" />}
                      {viewData.vital_signs.temperature && <VitalChip icon={<TempIcon />} label="Temp" value={`${viewData.vital_signs.temperature}°C`} color="#ef4444" />}
                      {viewData.vital_signs.respiratory_rate && <VitalChip icon={<LungIcon />} label="FR" value={`${viewData.vital_signs.respiratory_rate} irpm`} color="#3b82f6" />}
                      {viewData.vital_signs.oxygen_saturation && <VitalChip icon={<BloodIcon />} label="SpO₂" value={`${viewData.vital_signs.oxygen_saturation}%`} color="#8b5cf6" />}
                      {viewData.vital_signs.weight && <VitalChip icon={<WeightIcon />} label="Peso" value={`${viewData.vital_signs.weight} kg`} color="#14B8A6" />}
                      {viewData.vital_signs.height && <VitalChip icon={<HeightIcon />} label="Altura" value={`${viewData.vital_signs.height} cm`} color="#14B8A6" />}
                      {viewData.vital_signs.bmi && <VitalChip label="IMC" value={`${viewData.vital_signs.bmi}`} color="#0ea5e9" />}
                      {viewData.vital_signs.blood_glucose && <VitalChip label="Glicemia" value={`${viewData.vital_signs.blood_glucose} mg/dL`} color="#f97316" />}
                      {(viewData.vital_signs.pain_scale !== undefined && viewData.vital_signs.pain_scale !== null) && <VitalChip label="Dor" value={`${viewData.vital_signs.pain_scale}/10`} color="#dc2626" />}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Texto livre (evolução) */}
              {resolveEvolutionEntryFormat(viewData) === 'free_text' ? (
                <Card sx={{ mb: 2, borderRadius: 2, border: '1px solid #ccfbf1', bgcolor: '#f0fdfa' }}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f766e', mb: 1 }}>Evolução (texto livre)</Typography>
                    {viewData.evolution_free_text?.trim() ? (
                      <ClinicalHtmlField html={viewData.evolution_free_text} sx={{ color: 'inherit' }} />
                    ) : (
                      <Typography variant="body2" sx={{ color: '#94a3b8' }}>Sem texto registrado.</Typography>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <>
                  {viewData.subjective && <SoapSection letter="S" title="Subjetivo" content={viewData.subjective} color="#14B8A6" />}
                  {viewData.objective && <SoapSection letter="O" title="Objetivo" content={viewData.objective} color="#3b82f6" />}
                  {viewData.assessment && <SoapSection letter="A" title="Avaliação" content={viewData.assessment} color="#f59e0b" />}
                  {viewData.plan && <SoapSection letter="P" title="Plano" content={viewData.plan} color="#8b5cf6" />}
                </>
              )}

              {(() => {
                const labels = normalizeEvolutionProcedureLabels(viewData);
                if (!labels.length) return null;
                return (
                  <Card sx={{ mb: 2, borderRadius: 2, border: '1px solid #e0e7ff', bgcolor: '#f8fafc' }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#3730a3', mb: 1 }}>
                        Indicações (cirurgias e procedimentos)
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {labels.map((name, idx) => (
                          <Box key={`${idx}-${name}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}>
                            <Chip label={name} size="small" sx={{ fontWeight: 600, bgcolor: '#eef2ff', color: '#4338ca' }} />
                            <Tooltip title="Imprimir esta indicação">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void printProcedureLabel(name);
                                }}
                                sx={{ color: '#6366f1' }}
                                aria-label={`Imprimir indicação ${name}`}
                              >
                                <PrintIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                );
              })()}

              {(() => {
                const mats = getEvolutionWorkflowPrintables(viewData);
                if (!mats.length) return null;
                return (
                  <Card sx={{ mb: 2, borderRadius: 2, border: '1px solid #e2e8f0' }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1 }}>
                        Impressos e solicitações
                      </Typography>
                      <Stack spacing={1.5}>
                        {mats.map((p) => (
                          <Box key={p.clientKey} sx={{ borderLeft: '3px solid #14B8A6', pl: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#334155' }}>
                                  {evolutionPrintableListLabel(p)}
                                  {p.category !== 'prescription' ? (
                                    <Typography component="span" variant="caption" sx={{ color: '#94a3b8', ml: 1, fontWeight: 500 }}>
                                      ({printableCategoryLabel[p.category] || p.category})
                                    </Typography>
                                  ) : null}
                                </Typography>
                                {Boolean(String(p.bodyText || '').trim()) && (
                                  <ClinicalHtmlField
                                    html={sanitizeClinicalHtml(String(p.bodyText || '').replace(/\r\n|\r|\n/g, '<br/>'))}
                                    sx={{ fontSize: '0.8125rem', color: '#475569', mt: 0.5 }}
                                  />
                                )}
                              </Box>
                              <Tooltip title="Imprimir este documento">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void printAttachedDocuments([p]);
                                  }}
                                  sx={{ color: '#334155', flexShrink: 0 }}
                                  aria-label={`Imprimir ${evolutionPrintableListLabel(p)}`}
                                >
                                  <PrintIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Diagnosis */}
              {viewData.diagnosis && (
                <Card sx={{ mb: 2, borderRadius: 2, border: '1px solid #ede9fe' }}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#7c3aed', mb: 0.5 }}>Diagnóstico</Typography>
                    <Typography variant="body2">
                      {viewData.diagnosis}
                      {viewData.diagnosis_code && <Chip label={`CID: ${viewData.diagnosis_code}`} size="small" sx={{ ml: 1, bgcolor: '#ede9fe', color: '#7c3aed', fontWeight: 600 }} />}
                    </Typography>
                  </CardContent>
                </Card>
              )}

              {/* Follow-up */}
              {evolutionViewerHasFollowUpSection(viewData) && (
                <Card sx={{ mb: 2, borderRadius: 2, border: '1px solid #fef3c7' }}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#b45309', mb: 0.5 }}>Retorno</Typography>
                    {formatEvolutionReturnWorkflowLines(getEvolutionReturnWorkflow(viewData)).map((line) => (
                      <Typography key={line} variant="body2" sx={{ mb: 0.35 }}>
                        • {line}
                      </Typography>
                    ))}
                    {viewData.follow_up_notes && (
                      <Typography variant="body2" sx={{ mb: 0.5, whiteSpace: 'pre-wrap', mt: 0.25 }}>
                        {viewData.follow_up_notes}
                      </Typography>
                    )}
                    {viewData.follow_up_date && (
                      <Chip
                        icon={<CalendarIcon />}
                        label={new Date(viewData.follow_up_date + 'T12:00:00').toLocaleDateString('pt-BR')}
                        size="small"
                        sx={{ bgcolor: '#fef3c7', color: '#b45309', mt: 0.5 }}
                      />
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Internal Notes */}
              {viewData.internal_notes && (
                <Alert severity="info" sx={{ borderRadius: 2, mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Observações Internas</Typography>
                  <Typography variant="body2">{viewData.internal_notes}</Typography>
                </Alert>
              )}
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <SendDocumentDialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        documentType="evolution"
        documentId={sendDocData?.id || ''}
        patientId={sendDocData?.patientId}
        patientEmail={sendDocData?.email}
        patientPhone={sendDocData?.phone}
        patientWhatsapp={sendDocData?.whatsapp}
        patientName={sendDocData?.name}
        documentLabel="Evolução Clínica"
      />

      <EvolutionPrintablesWizard
        open={printWizardOpen}
        onClose={() => setPrintWizardOpen(false)}
        patientId={form.patient_id}
        professionalId={form.professional_id}
        initialPatient={toArray(patientOptions).find((p: any) => p.id === form.patient_id)}
        initialProfessional={toArray(profOptions).find((p: any) => p.id === form.professional_id)}
        onCommit={(item) => {
          setAttachedPrintables((prev) => [...prev, item]);
        }}
      />
      <EvolutionIndicationsDialog
        open={indicationsDialogOpen}
        onClose={() => setIndicationsDialogOpen(false)}
        onFinish={({ labels, printable }) => {
          setProcedureLabels((prev) => {
            const next = new Set(prev);
            for (const l of labels || []) {
              const t = String(l || '').trim();
              if (t) next.add(t);
            }
            return Array.from(next);
          });
          if (printable)
            setAttachedPrintables((prev) => [...prev, printable]);
        }}
      />

      {/* Dialog IA */}
      <Dialog
        open={aiDialogOpen}
        onClose={() => !aiGenerating && setAiDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3, overflow: 'hidden', m: isMobile ? 0 : 2, maxHeight: isMobile ? '100%' : undefined } }}
      >
        <Box sx={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)', px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AiIcon sx={{ color: '#fff' }} />
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>Gerar Evolução com IA</Typography>
          </Box>
          <IconButton onClick={() => !aiGenerating && setAiDialogOpen(false)} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
            Informe o nome do paciente e a queixa principal. A evolução será gerada em texto livre (incluindo estrutura tipo SOAP quando fizer sentido). Revise antes de concluir.
          </Typography>
          {aiError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{aiError}</Alert>}
          <TextField fullWidth label="Nome do Paciente" value={aiPatientName} onChange={e => setAiPatientName(e.target.value)}
            sx={{ mb: 2 }} placeholder="Ex: João Silva" disabled={aiGenerating} />
          <TextField fullWidth label="Queixa Principal / Motivo da Consulta" value={aiComplaint} onChange={e => setAiComplaint(e.target.value)}
            sx={{ mb: 2 }} multiline rows={2} placeholder="Ex: Dor lombar há 3 dias com irradiação para membro inferior esquerdo" disabled={aiGenerating} />
          <TextField fullWidth label="Histórico Médico / Informações Adicionais (opcional)" value={aiMedicalHistory} onChange={e => setAiMedicalHistory(e.target.value)}
            multiline rows={3} placeholder="Ex: Hipertenso, diabético tipo 2, cirurgia de hérnia discal em 2020, uso contínuo de Metformina 850mg" disabled={aiGenerating} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setAiDialogOpen(false)} disabled={aiGenerating} sx={{ color: '#64748b' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleAiGenerate} disabled={aiGenerating}
            startIcon={aiGenerating ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <AiIcon />}
            sx={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)', '&:hover': { background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)' }, borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}>
            {aiGenerating ? 'Gerando Evolução...' : 'Gerar com IA'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const SoapSection: React.FC<{ letter: string; title: string; content: string; color: string }> = ({ letter, title, content, color }) => (
  <Card sx={{ mb: 2, borderRadius: 2, borderLeft: `4px solid ${color}` }}>
    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Avatar sx={{ width: 24, height: 24, bgcolor: color, fontSize: 13, fontWeight: 700 }}>{letter}</Avatar>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color }}>{title}</Typography>
      </Box>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#334155' }}>{content}</Typography>
    </CardContent>
  </Card>
);

const VitalChip: React.FC<{ icon?: React.ReactElement; label: string; value: string; color: string }> = ({ icon, label, value, color }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: color + '10', border: `1px solid ${color}30`, borderRadius: 2, px: 1.5, py: 0.5 }}>
    {icon && React.cloneElement(icon, { sx: { fontSize: 16, color } })}
    <Box>
      <Typography variant="caption" sx={{ color: '#64748b', lineHeight: 1, display: 'block' }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, color, lineHeight: 1.2 }}>{value}</Typography>
    </Box>
  </Box>
);

export default Evolutions;
